import { AxiosError } from 'axios';

import { showToast } from '@/lib/utils';

import type { EntityScope, ServiceTypeAction } from './utils';

export interface ApiErrorMeta {
  entity: EntityScope;
  action: ServiceTypeAction;
}

const actionVerb: Record<ServiceTypeAction, string> = {
  list: 'load',
  read: 'load',
  create: 'create',
  update: 'update',
  delete: 'delete',
  clone: 'clone',
};

const entityLabel: Record<EntityScope, string> = {
  project: 'project',
  component: 'component',
  module: 'module',
  section: 'section',
  interface: 'interface',
  element: 'element',
  entity: 'entity',
};

export const extractApiErrorMessage = (error: unknown): string => {
  if (error instanceof AxiosError) {
    const responseMessage =
      error.response?.data &&
      typeof error.response.data === 'object' &&
      'message' in error.response.data
        ? String((error.response.data as { message?: unknown }).message)
        : undefined;
    return responseMessage || error.message || 'Unexpected API error.';
  }

  if (error instanceof Error) {
    return error.message;
  }

  return typeof error === 'string'
    ? error
    : 'Unexpected error occurred. Please try again.';
};

export const notifyApiError = (error: unknown, meta: ApiErrorMeta) => {
  const verb = actionVerb[meta.action] ?? 'process';
  const label = entityLabel[meta.entity] ?? 'resource';
  const description = extractApiErrorMessage(error);

  showToast({
    title: `Failed to ${verb} ${label}`,
    description,
    variant: 'destructive',
  });
};

export const shouldSuppressErrorToast = (meta?: unknown): boolean => {
  if (!meta || typeof meta !== 'object') return false;
  const flag = (meta as { suppressErrorToast?: unknown }).suppressErrorToast;
  return Boolean(flag);
};

